import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Chrome, 
  Download, 
  Shield, 
  Zap, 
  Eye, 
  DollarSign, 
  ArrowRight, 
  Check, 
  Star,
  ShoppingCart,
  TrendingDown,
  Bell,
  Settings,
  Sparkles,
  Package,
  BarChart3,
  Code2
} from 'lucide-react';
import { CILogo } from '../components/CILogo';

export default function ExtensionPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: DollarSign,
      title: 'Smart Price Comparison',
      description: 'See prices for the same or equivalent product across major retailers — without opening ten tabs or digging through Google Shopping.'
    },
    {
      icon: Eye,
      title: 'Live Social Proof (for Integrated Brands)',
      description: 'On partner sites, see how many people have viewed and purchased the product recently. Real events, not fake counters.'
    },
    {
      icon: TrendingDown,
      title: 'Confidence & Savings Alerts',
      description: 'Get subtle nudges when you are about to overpay or when you are already in line with the best available offer.'
    },
    {
      icon: Settings,
      title: 'Minimal, Flexible UI',
      description: 'Choose between a compact floating widget or a side panel when you want the full picture. Always there when you need it, out of the way when you do not.'
    },
    {
      icon: Package,
      title: 'Richer Product Context',
      description: 'See product image, key details, and shipping information alongside prices so you can decide faster with more context.'
    },
    {
      icon: Shield,
      title: 'Privacy-First',
      description: 'We focus on product and price intelligence, not your identity. No cross-site personal tracking — just better commerce signals.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Install the extension',
      description: 'One click from the Chrome Web Store. It is free.'
    },
    {
      number: '2',
      title: 'Browse as usual',
      description: 'Search on Google, click product links, and shop your favorite sites exactly like you do today.'
    },
    {
      number: '3',
      title: 'See prices and proof',
      description: 'On supported pages, Commerce Index Shopper shows you other prices for the same product and, for integrated brands, live proof of how many people have viewed and purchased recently.'
    },
    {
      number: '4',
      title: 'Decide with confidence',
      description: 'Make every product decision with more context — price, proof, and soon, Commerce Index category signals.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B1120] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B1120]/95 backdrop-blur-sm border-b border-[#22304A]">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <CILogo size={32} variant="primary" theme="dark" />
              <span className="font-semibold text-lg">Commerce Index</span>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/proof')} className="text-[#94A3B8] hover:text-white transition-colors">
                CI Proof
              </button>
              <button onClick={() => navigate('/brands')} className="text-[#94A3B8] hover:text-white transition-colors">
                For Brands
              </button>
              <button onClick={() => navigate('/docs')} className="text-[#94A3B8] hover:text-white transition-colors">
                Docs
              </button>
              <Button 
                className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90"
                onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}
              >
                <Chrome className="w-4 h-4 mr-2" />
                Install Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      {/* MOBILE OVERFLOW FIX: clip decorative blurred circles that extend past the
          viewport edge (absolute -right-4) and let grid tracks shrink (min-w-0) */}
      <section className="pt-32 pb-20 px-6 overflow-hidden">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="min-w-0">
              <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-6">
                <Chrome className="w-3 h-3 mr-1" />
                Chrome Extension
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Shop with Better Signals, 
                <span className="text-[#3EF1C7]"> Not Just Better Prices</span>
              </h1>
              <p className="text-xl text-[#94A3B8] mb-8">
                The <strong className="text-white">Commerce Index Shopper</strong> Chrome Extension shows you prices for the same product across top retailers — 
                and, for integrated brands, real-time proof of how many people are viewing and buying. 
                All without changing how you browse.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 font-semibold"
                  onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}
                  data-testid="install-extension-hero"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Install for Free on Chrome
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#334155] text-white hover:bg-[#1E293B]"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                >
                  See How It Works
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <p className="text-sm text-[#64748B] mt-6">
                Powered by <strong className="text-[#94A3B8]">Commerce Index</strong> — the same data that powers CI Proof for brands, dashboards for teams, and Signals APIs for developers.
              </p>
            </div>
            
            {/* Widget Preview Mock */}
            <div className="relative min-w-0">{/* min-w-0: see hero overflow fix above */}
              <div className="bg-[#1E293B] rounded-2xl border border-[#334155] p-6 shadow-2xl">
                {/* Mock browser chrome */}
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-[#334155]">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                    <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                    <div className="w-3 h-3 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="flex-1 bg-[#0B1120] rounded-lg px-4 py-1.5 text-sm text-[#64748B] ml-4">
                    amazon.com/dp/B0C1WXV7RY
                  </div>
                </div>
                
                {/* Mock Widget */}
                <div className="bg-[#0D1628] rounded-xl border border-[#22304A] overflow-hidden">
                  <div className="flex items-center justify-between p-3 border-b border-[#22304A]">
                    <div className="flex items-center gap-2">
                      <CILogo size={20} variant="primary" theme="dark" />
                      <span className="text-sm font-medium">Live activity on this product</span>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-[#E2E8F0]">
                        <Eye className="w-4 h-4 text-[#3EF1C7]" />
                        <span><strong>37 people</strong> viewed this in the last hour</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#E2E8F0] mt-1">
                        <ShoppingCart className="w-4 h-4 text-[#3EF1C7]" />
                        <span><strong>12 purchases</strong> in the last 24 hours</span>
                      </div>
                      <p className="text-sm text-[#FFD166] mt-2 flex items-center gap-1">
                        <Sparkles className="w-4 h-4" />
                        This product is getting a lot of attention.
                      </p>
                    </div>
                    <div className="text-xs text-[#64748B] uppercase tracking-wide">Better prices found:</div>
                    {[
                      { store: 'Walmart', price: '$559.99', savings: '-$89' },
                      { store: 'Best Buy', price: '$599.99', savings: '-$49' },
                      { store: 'Target', price: '$619.99', savings: '-$29' },
                    ].map((offer, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-[#1E293B] rounded-lg border border-[#334155]">
                        <span className="text-sm text-white">{offer.store}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[#3EF1C7] font-semibold">{offer.price}</span>
                          <span className="text-[#8CF78E] text-xs bg-[#8CF78E]/15 px-2 py-0.5 rounded">{offer.savings}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 border-t border-[#22304A] text-center text-xs text-[#64748B]">
                    Powered by Commerce Index
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#3EF1C7]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#7A5CFF]/20 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
              Features
            </Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What You Get with Commerce Index Shopper
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card key={i} className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-[#3EF1C7]/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#3EF1C7]" />
                    </div>
                    <CardTitle className="text-white text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#94A3B8] text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6 bg-[#0D1628]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 mb-4">
              How It Works
            </Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              How Commerce Index Shopper Works
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#3EF1C7] text-[#0B1120] font-bold text-xl flex items-center justify-center mx-auto mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                  <p className="text-[#94A3B8] text-sm">{step.description}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-6 left-[calc(50%+30px)] w-[calc(100%-60px)] h-0.5 bg-[#22304A]">
                    <ArrowRight className="absolute -right-2 -top-2 w-4 h-4 text-[#3EF1C7]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commerce Index Product Stack */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              The Commerce Index Product Stack
            </h2>
            <p className="text-[#94A3B8]">
              One stack for shoppers, brands, and builders — all powered by the same Commerce Index event graph.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* CI Shopper */}
            <Card className="bg-[#0D1628] border-[#3EF1C7] border-2">
              <CardHeader>
                <Chrome className="w-8 h-8 text-[#3EF1C7] mb-2" />
                <CardTitle className="text-white">Commerce Index Shopper</CardTitle>
                <Badge className="w-fit bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30">Chrome Extension · For Shoppers</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm mb-4">
                  Compare prices across retailers and see live proof on partner sites — directly on product pages.
                </p>
                <Link to="/extension" className="text-[#3EF1C7] text-sm hover:underline">You are here →</Link>
              </CardContent>
            </Card>
            
            {/* CI Proof */}
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#7A5CFF]/50 transition-colors">
              <CardHeader>
                <Eye className="w-8 h-8 text-[#7A5CFF] mb-2" />
                <CardTitle className="text-white">CI Proof</CardTitle>
                <Badge className="w-fit bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30">Live Activity Widget · For Brands</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm mb-4">
                  Add a real-time activity widget to your PDPs. Powered by real events, not fake counters.
                </p>
                <Link to="/proof" className="text-[#7A5CFF] text-sm hover:underline">View Proof for Brands →</Link>
              </CardContent>
            </Card>
            
            {/* CI for Brands */}
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#FFD166]/50 transition-colors">
              <CardHeader>
                <BarChart3 className="w-8 h-8 text-[#FFD166] mb-2" />
                <CardTitle className="text-white">CI for Brands</CardTitle>
                <Badge className="w-fit bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30">Dashboards · Coming Soon</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm mb-4">
                  Turn the events you send to CI into brand-level dashboards and product-level insights.
                </p>
                <Link to="/brands" className="text-[#FFD166] text-sm hover:underline">Join CI for Brands Waitlist →</Link>
              </CardContent>
            </Card>
            
            {/* CI Signals */}
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors">
              <CardHeader>
                <Code2 className="w-8 h-8 text-[#3EF1C7] mb-2" />
                <CardTitle className="text-white">Commerce Index Signals</CardTitle>
                <Badge className="w-fit bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30">API · For Developers</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm mb-4">
                  API-first access to brand, product, and category signals for your own tools and platforms.
                </p>
                <Link to="/signals" className="text-[#3EF1C7] text-sm hover:underline">Explore CI Signals →</Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0D1628] to-[#0B1120]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Ready to Start Using Commerce Index?
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8 max-w-2xl mx-auto">
            Install the Chrome extension to shop with better signals today. 
            Talk to us about CI Proof, CI for Brands, or CI Signals if you are building on the brand or platform side.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 font-semibold"
              onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}
              data-testid="install-extension-cta"
            >
              <Chrome className="w-5 h-5 mr-2" />
              Install Commerce Index Shopper
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-[#334155] text-white hover:bg-[#1E293B]"
              onClick={() => navigate('/proof')}
            >
              CI Proof for Brands
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-[#334155] text-white hover:bg-[#1E293B]"
              onClick={() => navigate('/docs/signals')}
            >
              CI Signals for Developers
            </Button>
          </div>
          <p className="text-[#64748B] text-sm mt-6">
            Free forever • No account required • Privacy focused
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#22304A]">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CILogo size={32} variant="primary" theme="dark" />
              <span className="font-semibold">Commerce Index</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-[#94A3B8]">
              <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button>
              <button onClick={() => navigate('/contact')} className="hover:text-white transition-colors">Support</button>
              <button onClick={() => navigate('/docs')} className="hover:text-white transition-colors">Documentation</button>
            </div>
          </div>
          <p className="text-center text-[#64748B] text-sm mt-8">
            © 2024 Commerce Index. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
